import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Checkbox } from './ui/checkbox';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Key, Plus, Loader2, Trash2, Copy, Webhook, Link2, AlertTriangle, CheckCircle2, Eye, EyeOff } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const SCOPES = [
  { id: 'read', label: 'Read', desc: 'Read properties, platforms, contacts, KB, training links' },
  { id: 'write', label: 'Write', desc: 'Create incidents, submit feedback, push usage events' },
  { id: 'webhooks', label: 'Webhooks', desc: 'Receive push notifications when data changes' },
];

const WEBHOOK_EVENTS = [
  { id: 'assignment.updated', label: 'Assignment Updated' },
  { id: 'status.changed', label: 'System Status Changed' },
  { id: 'incident.created', label: 'Incident Created' },
  { id: 'kb.updated', label: 'Knowledge Base Updated' },
  { id: 'governance.updated', label: 'Governance Updated' },
  { id: 'property.created', label: 'Property Created' },
];

export default function IntegrationSettings({ token }) {
  const [keys, setKeys] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [keyDialog, setKeyDialog] = useState(false);
  const [webhookDialog, setWebhookDialog] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [keyForm, setKeyForm] = useState({ name: '', platform: 'opthinx', scopes: ['read', 'write'] });
  const [whForm, setWhForm] = useState({ url: '', events: ['assignment.updated', 'status.changed', 'incident.created'] });
  const [saving, setSaving] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchAll = useCallback(async () => {
    try {
      const [k, w] = await Promise.all([
        axios.get(`${BACKEND}/api/admin/integration-keys`, { headers }),
        axios.get(`${BACKEND}/api/admin/webhooks`, { headers }),
      ]);
      setKeys(k.data.keys || []);
      setWebhooks(w.data.webhooks || []);
    } catch {} finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const createKey = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${BACKEND}/api/admin/integration-keys`, keyForm, { headers });
      setNewKey(res.data);
      setKeyDialog(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const revokeKey = async (id) => {
    if (!window.confirm('Revoke this API key? Any integration using it will stop working.')) return;
    try { await axios.delete(`${BACKEND}/api/admin/integration-keys/${id}`, { headers }); toast.success('Key revoked'); fetchAll(); }
    catch { toast.error('Failed'); }
  };

  const createWebhook = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${BACKEND}/api/admin/webhooks`, whForm, { headers });
      toast.success('Webhook created');
      setWebhookDialog(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const deleteWebhook = async (id) => {
    try { await axios.delete(`${BACKEND}/api/admin/webhooks/${id}`, { headers }); toast.success('Deleted'); fetchAll(); }
    catch { toast.error('Failed'); }
  };

  const toggleScope = (scope) => setKeyForm(p => ({ ...p, scopes: p.scopes.includes(scope) ? p.scopes.filter(s => s !== scope) : [...p.scopes, scope] }));
  const toggleEvent = (ev) => setWhForm(p => ({ ...p, events: p.events.includes(ev) ? p.events.filter(e => e !== ev) : [...p.events, ev] }));

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-8" data-testid="integration-settings">
      <div>
        <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Integrations & API</h2>
        <p className="text-sm text-muted-foreground">Manage API keys for external platforms (OpThinx, custom integrations) and configure webhooks for real-time data sync.</p>
      </div>

      {/* New key reveal */}
      {newKey && (
        <div className="p-4 rounded-xl border-2 border-amber-300 bg-amber-50">
          <div className="flex items-center gap-2 mb-2"><AlertTriangle className="h-4 w-4 text-amber-700" /><h3 className="text-sm font-semibold text-amber-800">Save your API key now — it won't be shown again</h3></div>
          <div className="flex items-center gap-2 bg-white p-3 rounded-lg border font-mono text-sm">
            <code className="flex-1 break-all">{newKey.key}</code>
            <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => { navigator.clipboard.writeText(newKey.key); toast.success('Copied'); }}><Copy className="h-3.5 w-3.5" /></Button>
          </div>
          <Button variant="ghost" size="sm" className="mt-2 text-xs" onClick={() => setNewKey(null)}>I've saved it — dismiss</Button>
        </div>
      )}

      {/* API Keys */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold flex items-center gap-2"><Key className="h-4 w-4" /> API Keys</h3>
          <Button variant="outline" size="sm" onClick={() => setKeyDialog(true)} className="gap-1 text-xs" data-testid="create-api-key-btn"><Plus className="h-3 w-3" /> Generate Key</Button>
        </div>
        {keys.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6 border rounded-lg">No API keys created yet. Generate one to connect OpThinx or other platforms.</p>
        ) : (
          <div className="space-y-2">
            {keys.map(k => (
              <div key={k.id} className="p-3 rounded-lg border bg-card flex items-center gap-3">
                <Key className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{k.name}</span>
                    <Badge variant="outline" className="text-[9px]">{k.platform}</Badge>
                    <Badge className={`text-[9px] ${k.active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{k.active ? 'Active' : 'Revoked'}</Badge>
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 text-[10px] text-muted-foreground">
                    <span className="font-mono">{k.key_prefix}...</span>
                    <span>Scopes: {(k.scopes || []).join(', ')}</span>
                    {k.last_used && <span>Last used: {new Date(k.last_used).toLocaleDateString()}</span>}
                  </div>
                </div>
                {k.active && <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => revokeKey(k.id)}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>}
              </div>
            ))}
          </div>
        )}
      </div>

      <Separator />

      {/* Webhooks */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold flex items-center gap-2"><Webhook className="h-4 w-4" /> Webhooks</h3>
          <Button variant="outline" size="sm" onClick={() => setWebhookDialog(true)} className="gap-1 text-xs" data-testid="create-webhook-btn"><Plus className="h-3 w-3" /> Add Webhook</Button>
        </div>
        {webhooks.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6 border rounded-lg">No webhooks configured. Add one to push real-time updates to OpThinx.</p>
        ) : (
          <div className="space-y-2">
            {webhooks.map(w => (
              <div key={w.id} className="p-3 rounded-lg border bg-card flex items-center gap-3">
                <Webhook className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-mono truncate block">{w.url}</span>
                  <div className="flex gap-1 mt-1">{(w.events || []).map(e => <Badge key={e} variant="secondary" className="text-[8px]">{e}</Badge>)}</div>
                </div>
                <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => deleteWebhook(w.id)}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Separator />

      {/* API Reference */}
      <div className="p-4 rounded-xl border bg-slate-50/50">
        <h3 className="text-sm font-semibold mb-2 flex items-center gap-2"><Link2 className="h-4 w-4" /> API Reference</h3>
        <div className="space-y-1 text-xs font-mono text-muted-foreground">
          <p className="font-sans text-xs text-foreground font-medium mb-2">All endpoints require <code className="bg-muted px-1 py-0.5 rounded">X-Api-Key</code> header.</p>
          <p><Badge variant="outline" className="text-[8px] mr-1">GET</Badge> /api/v1/properties</p>
          <p><Badge variant="outline" className="text-[8px] mr-1">GET</Badge> /api/v1/properties/:id/platforms</p>
          <p><Badge variant="outline" className="text-[8px] mr-1">GET</Badge> /api/v1/properties/:id/contacts</p>
          <p><Badge variant="outline" className="text-[8px] mr-1">GET</Badge> /api/v1/knowledge-base</p>
          <p><Badge variant="outline" className="text-[8px] mr-1">GET</Badge> /api/v1/training-links</p>
          <p><Badge variant="outline" className="text-[8px] mr-1">GET</Badge> /api/v1/system-status</p>
          <p><Badge variant="outline" className="text-[8px] mr-1 bg-blue-50">POST</Badge> /api/v1/incidents <span className="text-slate-400">(write scope)</span></p>
          <p><Badge variant="outline" className="text-[8px] mr-1 bg-blue-50">POST</Badge> /api/v1/vendor-feedback <span className="text-slate-400">(write scope)</span></p>
          <p><Badge variant="outline" className="text-[8px] mr-1 bg-blue-50">POST</Badge> /api/v1/usage-events <span className="text-slate-400">(write scope)</span></p>
        </div>
      </div>

      {/* Create Key Dialog */}
      <Dialog open={keyDialog} onOpenChange={setKeyDialog}>
        <DialogContent className="max-w-md"><DialogHeader><DialogTitle>Generate API Key</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-xs">Key Name</Label><Input value={keyForm.name} onChange={e => setKeyForm(p => ({ ...p, name: e.target.value }))} placeholder="OpThinx Production" className="mt-1" data-testid="api-key-name" /></div>
            <div><Label className="text-xs">Platform</Label>
              <Select value={keyForm.platform} onValueChange={v => setKeyForm(p => ({ ...p, platform: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="opthinx">OpThinx</SelectItem><SelectItem value="custom">Custom Integration</SelectItem><SelectItem value="zapier">Zapier</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Scopes</Label>
              <div className="space-y-2 mt-1">{SCOPES.map(s => (
                <label key={s.id} className="flex items-center gap-2 p-2 rounded border hover:bg-muted/40 cursor-pointer">
                  <Checkbox checked={keyForm.scopes.includes(s.id)} onCheckedChange={() => toggleScope(s.id)} />
                  <div><span className="text-sm font-medium">{s.label}</span><p className="text-[10px] text-muted-foreground">{s.desc}</p></div>
                </label>
              ))}</div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setKeyDialog(false)}>Cancel</Button><Button onClick={createKey} disabled={saving || !keyForm.name}>{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Key className="h-4 w-4 mr-1" />}Generate Key</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Webhook Dialog */}
      <Dialog open={webhookDialog} onOpenChange={setWebhookDialog}>
        <DialogContent className="max-w-md"><DialogHeader><DialogTitle>Add Webhook</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-xs">Endpoint URL</Label><Input value={whForm.url} onChange={e => setWhForm(p => ({ ...p, url: e.target.value }))} placeholder="https://opthinx.com/api/webhooks/lodgiflow" className="mt-1 font-mono text-xs" data-testid="webhook-url" /></div>
            <div><Label className="text-xs">Events</Label>
              <div className="space-y-1 mt-1">{WEBHOOK_EVENTS.map(e => (
                <label key={e.id} className="flex items-center gap-2 p-2 rounded border hover:bg-muted/40 cursor-pointer">
                  <Checkbox checked={whForm.events.includes(e.id)} onCheckedChange={() => toggleEvent(e.id)} />
                  <span className="text-sm">{e.label}</span>
                </label>
              ))}</div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setWebhookDialog(false)}>Cancel</Button><Button onClick={createWebhook} disabled={saving || !whForm.url}>{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Create Webhook</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
